import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { MdArrowBack, MdSave, MdImage, MdInventory2, MdDesignServices, MdDelete } from 'react-icons/md';
import { inventarioAPI } from '../services/api';
import { useModal } from '../context/ModalContext';

const Container = styled.div`width: 100%; max-width: 100%;`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl};
  flex-wrap: wrap;
`;

const HeaderLeft = styled.div`display: flex; align-items: center; gap: ${p => p.theme.spacing.lg};`;

const BackButton = styled.button`
  width: 40px; height: 40px;
  display: flex; align-items: center; justify-content: center;
  background: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  color: ${p => p.theme.colors.textPrimary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  svg { font-size: 1.5rem; }
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
`;

const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
  display: flex; align-items: center; gap: 10px;
  svg { color: ${p => p.theme.colors.primary}; }
`;

const DeleteBtn = styled.button`
  padding: 8px 16px;
  border: 1px solid ${p => p.theme.colors.danger}50;
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.danger}10;
  color: ${p => p.theme.colors.danger};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: flex; align-items: center; gap: 6px;
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  svg { font-size: 1.1rem; }
  &:hover { background: ${p => p.theme.colors.danger}20; }
`;

const FormCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: ${p => p.theme.spacing.xl};
  @media (max-width: 760px) { grid-template-columns: 1fr; }
`;

const ImagePanel = styled.div``;

const ImageDrop = styled.div`
  width: 100%;
  aspect-ratio: 1;
  border: 2px dashed ${p => p.has ? p.theme.colors.primary : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${p => p.theme.spacing.md};
  cursor: pointer;
  overflow: hidden;
  background: ${p => p.theme.colors.background};
  transition: all 0.2s;
  &:hover { border-color: ${p => p.theme.colors.primary}; }
  img { width: 100%; height: 100%; object-fit: cover; }
  svg { font-size: 3rem; color: ${p => p.theme.colors.textLight}; opacity: 0.5; }
  p { font-size: ${p => p.theme.fontSizes.sm}; color: ${p => p.theme.colors.textSecondary}; margin: 0; }
`;

const ImgHint = styled.p`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
  margin: 8px 0 0;
  text-align: center;
`;

const Fields = styled.div`display: flex; flex-direction: column; gap: ${p => p.theme.spacing.lg};`;
const FieldGroup = styled.div`display: flex; flex-direction: column; gap: ${p => p.theme.spacing.sm};`;

const Label = styled.label`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.medium};
  color: ${p => p.theme.colors.textPrimary};
`;

const Input = styled.input`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const Select = styled.select`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;

const TextArea = styled.textarea`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  font-family: ${p => p.theme.fonts.primary};
  min-height: 100px;
  resize: vertical;
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${p => p.theme.spacing.md};
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  cursor: pointer;
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  input { display: none; }
  .track {
    width: 44px; height: 24px;
    border-radius: 99px;
    background: ${p => p.checked ? p.theme.colors.primary : p.theme.colors.border};
    position: relative;
    transition: background 0.2s;
  }
  .thumb {
    position: absolute;
    top: 3px;
    left: ${p => p.checked ? '23px' : '3px'};
    width: 18px; height: 18px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${p => p.theme.spacing.xl};
  padding-top: ${p => p.theme.spacing.xl};
  border-top: 1px solid ${p => p.theme.colors.border};
`;

const Btn = styled.button`
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.xl};
  background: ${p => p.primary
    ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
    : p.theme.colors.backgroundLight};
  color: ${p => p.primary ? 'white' : p.theme.colors.textPrimary};
  border: 1px solid ${p => p.primary ? p.theme.colors.primary : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: flex; align-items: center; gap: 6px;
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { transform: translateY(-1px); }
`;

const ErrMsg = styled.p`
  color: ${p => p.theme.colors.danger};
  font-size: ${p => p.theme.fontSizes.sm};
  padding: ${p => p.theme.spacing.md};
  background: ${p => p.theme.colors.danger}10;
  border-radius: ${p => p.theme.borderRadius.md};
  border-left: 3px solid ${p => p.theme.colors.danger};
  margin: 0;
`;

const InventarioItemDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { confirm, toast } = useModal();
  const imgRef = useRef(null);

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const [imgFile, setImgFile] = useState(null);

  const [form, setForm] = useState({
    nombre: '', descripcion: '', precio: '', sku: '',
    stock: '0', stock_minimo: '5', duracion_minutos: '', categoria: '', activo: true,
  });

  useEffect(() => {
    inventarioAPI.getById(id)
      .then(res => {
        const d = res.data.data;
        setItem(d);
        setForm({
          nombre: d.nombre || '',
          descripcion: d.descripcion || '',
          precio: d.precio != null ? String(d.precio) : '',
          sku: d.sku || '',
          stock: d.stock != null ? String(d.stock) : '0',
          stock_minimo: d.stock_minimo != null ? String(d.stock_minimo) : '5',
          duracion_minutos: d.duracion_minutos != null ? String(d.duracion_minutos) : '',
          duracion_unidad: d.duracion_unidad || 'minutos',
          categoria: d.categoria || '',
          activo: !!d.activo,
        });
        if (d.imagen_archivo) setImgPreview(inventarioAPI.getImageUrl(d.imagen_archivo));
      })
      .catch(() => toast('Error al cargar', 'error'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  const isProducto = item?.tipo === 'producto';

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleImage = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setImgFile(file);
    const reader = new FileReader();
    reader.onload = e => setImgPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.nombre.trim()) { setError('El nombre es obligatorio'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('nombre', form.nombre.trim());
      if (form.descripcion) fd.append('descripcion', form.descripcion);
      if (form.precio) fd.append('precio', form.precio);
      if (form.categoria) fd.append('categoria', form.categoria);
      fd.append('activo', form.activo ? '1' : '0');
      if (isProducto) {
        if (form.sku) fd.append('sku', form.sku);
        fd.append('stock', form.stock || '0');
        fd.append('stock_minimo', form.stock_minimo || '5');
      } else {
        if (form.duracion_minutos) fd.append('duracion_minutos', form.duracion_minutos);
        fd.append('duracion_unidad', form.duracion_unidad || 'minutos');
      }
      if (imgFile) fd.append('imagen', imgFile);
      await inventarioAPI.update(id, fd);
      toast('Guardado correctamente', 'success');
      navigate('/inventario');
    } catch (err) {
      setError(err.message || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm(`¿Eliminar "${item?.nombre}"?`, {
      title: `Eliminar ${isProducto ? 'producto' : 'servicio'}`,
      confirmText: 'Eliminar',
      danger: true,
    });
    if (!ok) return;
    try {
      await inventarioAPI.delete(id);
      toast('Eliminado', 'success');
      navigate('/inventario');
    } catch (err) {
      toast(err.message || 'Error', 'error');
    }
  };

  if (loading) return <Container><p>Cargando...</p></Container>;
  if (!item) return <Container><p>Item no encontrado.</p></Container>;

  return (
    <Container>
      <PageHeader>
        <HeaderLeft>
          <BackButton onClick={() => navigate(-1)}><MdArrowBack /></BackButton>
          <Title>
            {isProducto ? <MdInventory2 /> : <MdDesignServices />}
            {form.nombre || 'Item'}
          </Title>
        </HeaderLeft>
        <DeleteBtn onClick={handleDelete}><MdDelete /> Eliminar</DeleteBtn>
      </PageHeader>

      <FormCard>
        <form onSubmit={handleSubmit}>
          <Grid>
            <ImagePanel>
              <Label>Imagen</Label>
              <ImageDrop
                has={!!imgPreview}
                onClick={() => imgRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); handleImage(e.dataTransfer.files[0]); }}
              >
                {imgPreview
                  ? <img src={imgPreview} alt="preview" />
                  : (<><MdImage /><p>Sube una imagen</p></>)
                }
              </ImageDrop>
              <ImgHint>Click para cambiar — JPG, PNG, WebP</ImgHint>
              <input ref={imgRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => handleImage(e.target.files[0])} />
            </ImagePanel>

            <Fields>
              <FieldGroup>
                <Label>Nombre *</Label>
                <Input name="nombre" value={form.nombre} onChange={handleChange} required />
              </FieldGroup>

              <FieldGroup>
                <Label>Descripcion</Label>
                <TextArea name="descripcion" value={form.descripcion} onChange={handleChange} />
              </FieldGroup>

              <Row>
                <FieldGroup>
                  <Label>Precio</Label>
                  <Input type="number" name="precio" value={form.precio} onChange={handleChange} min="0" step="0.01" />
                </FieldGroup>
                <FieldGroup>
                  <Label>Categoria</Label>
                  <Input name="categoria" value={form.categoria} onChange={handleChange} />
                </FieldGroup>
              </Row>

              {isProducto ? (
                <Row>
                  <FieldGroup>
                    <Label>SKU</Label>
                    <Input name="sku" value={form.sku} onChange={handleChange} />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Stock</Label>
                    <Input type="number" name="stock" value={form.stock} onChange={handleChange} min="0" />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Stock minimo</Label>
                    <Input type="number" name="stock_minimo" value={form.stock_minimo} onChange={handleChange} min="0" />
                  </FieldGroup>
                </Row>
              ) : (
                <Row>
                  <FieldGroup>
                    <Label>Duracion</Label>
                    <Input type="number" name="duracion_minutos" value={form.duracion_minutos}
                      onChange={handleChange} min="1" />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Unidad</Label>
                    <Select name="duracion_unidad" value={form.duracion_unidad} onChange={handleChange}>
                      <option value="minutos">Minutos</option>
                      <option value="horas">Horas</option>
                      <option value="dias">Dias</option>
                      <option value="semanas">Semanas</option>
                      <option value="meses">Meses</option>
                      <option value="años">Años</option>
                    </Select>
                  </FieldGroup>
                </Row>
              )}

              <Toggle checked={form.activo}>
                <input type="checkbox" name="activo" checked={form.activo} onChange={handleChange} />
                <div className="track"><div className="thumb" /></div>
                {form.activo ? 'Activo' : 'Inactivo'}
              </Toggle>

              {error && <ErrMsg>{error}</ErrMsg>}
            </Fields>
          </Grid>

          <ButtonRow>
            <Btn type="button" onClick={() => navigate(-1)} disabled={submitting}>Cancelar</Btn>
            <Btn type="submit" primary disabled={submitting}>
              <MdSave /> {submitting ? 'Guardando...' : 'Guardar cambios'}
            </Btn>
          </ButtonRow>
        </form>
      </FormCard>
    </Container>
  );
};

export default InventarioItemDetail;
